import { useState, useEffect, useCallback } from 'react';
import { useAppSelector } from '../../../../../../app/store';
import { useGetAddressTypesQuery } from '../../../../../../services/RegistrationService';
import { AddressType } from '../../../../../../services/RegistrationService/registrationSlice';

const createInitialFormData = (): AddressType => ({
  addressType: '',
  street: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  isPreferred: false,
});

export const useAddressForm = (address: AddressType | null | undefined, open: boolean) => {
  const { data: addressTypes = [], isLoading } = useGetAddressTypesQuery();
  const addresses = useAppSelector(state => state.registration.currentRegistration?.addresses) || [];

  // Exclude address types already present, except for the one being edited
  const usedTypes = addresses
    .filter(addr => !address || addr.addressType !== address.addressType)
    .map(addr => addr.addressType);
  const availableAddressTypes = addressTypes.filter(type => !usedTypes.includes(type.value));

  const [formData, setFormData] = useState<AddressType>(createInitialFormData());

  useEffect(() => {
    if (address) {
      setFormData({
        addressType: address.addressType || '',
        street: address.street || '',
        street2: address.street2 || '',
        city: address.city || '',
        state: address.state || '',
        zip: address.zip || '',
        isPreferred: address.isPreferred || false,
      });
    } else {
      setFormData(createInitialFormData());
    }
  }, [address, open]);

  const handleChange = useCallback((field: keyof AddressType) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (field === 'isPreferred') {
      setFormData(prev => ({ ...prev, [field]: e.target.checked }));
    } else {
      setFormData(prev => ({ ...prev, [field]: e.target.value }));
    }
  }, []);

  const resetForm = useCallback(() => {
    setFormData(createInitialFormData());
  }, []);

  return {
    formData,
    availableAddressTypes,
    isLoading,
    handleChange,
    resetForm,
  };
};
